import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@produzionejb/ui';
import type { SapCreateRequest, SapLine } from '@produzionejb/types';
import { ApiError } from '../../lib/api-client';
import {
  useCreateSap,
  useSap,
  useUpdateSap,
  useCloseSap,
  useDeleteSap,
} from '../../lib/sap-api';
import { LotPicker } from '../../components/LotPicker';
import { ProductPicker } from '../../components/ProductPicker';
import { BackToStockMovementsLink } from '../../components/BackToStockMovementsLink';

type SapRow = {
  key: string;
  lotId: string;
  lotCode?: string;
  productId: string | null;
  productCode?: string | null;
  productName?: string | null;
  qty: string;
  note?: string;
};

function newKey() {
  return Math.random().toString(36).slice(2, 9);
}

function todayIso() {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function sumNumStrings(rows: { qty: string }[]): number {
  return rows.reduce((acc, r) => acc + (Number(r.qty) || 0), 0);
}

export function SapFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const sapQ = useSap(id);
  const createMut = useCreateSap();
  const updateMut = useUpdateSap(id ?? '');
  const closeMut = useCloseSap(id ?? '');
  const deleteMut = useDeleteSap();

  const [draftName, setDraftName] = useState('');
  const [responsable, setResponsable] = useState('');
  const [workDate, setWorkDate] = useState(todayIso());
  const [notes, setNotes] = useState('');
  const [righe, setRighe] = useState<SapRow[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const readOnly = isEdit && sapQ.data?.status !== 'DRAFT';

  useEffect(() => {
    if (isEdit && sapQ.data) {
      const s = sapQ.data;
      setDraftName(s.draftName ?? '');
      setResponsable(s.responsable ?? '');
      setWorkDate(s.workDate.slice(0, 10));
      setNotes(s.notes ?? '');
      setRighe(
        s.righe.map((l) => ({
          key: l.id,
          lotId: l.lotId ?? '',
          lotCode: l.lotCode ?? undefined,
          productId: l.productId ?? null,
          productCode: l.productCode,
          productName: l.productName,
          qty: l.qty,
          note: l.note ?? '',
        })),
      );
    }
  }, [isEdit, sapQ.data]);

  const totQty = useMemo(() => sumNumStrings(righe), [righe]);

  function addRiga() {
    setRighe((rows) => [...rows, { key: newKey(), lotId: '', productId: null, qty: '' }]);
  }
  function removeRiga(key: string) {
    setRighe((rows) => rows.filter((r) => r.key !== key));
  }
  function updateRiga(key: string, patch: Partial<SapRow>) {
    setRighe((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function buildPayload(): SapCreateRequest {
    return {
      draftName: draftName.trim(),
      responsable: responsable.trim() || null,
      workDate: new Date(workDate),
      notes: notes.trim() || null,
      righe: righe
        .filter((r) => r.lotId && r.qty)
        .map<SapLine>((r) => ({
          lotId: r.lotId,
          productId: r.productId,
          qty: Number(r.qty),
          note: r.note?.trim() || null,
        })),
    };
  }

  async function handleSave() {
    setErrorMsg(null);
    const payload = buildPayload();
    if (!payload.draftName) {
      setErrorMsg(t('sap.form.errorDraftNameRequired'));
      return;
    }
    try {
      if (isEdit && id) {
        await updateMut.mutateAsync(payload);
      } else {
        const created = await createMut.mutateAsync(payload);
        navigate(`/documents/sap/${created.id}/edit`);
        return;
      }
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  async function handleClose() {
    if (!id) return;
    if (righe.length === 0) {
      setErrorMsg(t('sap.form.errorEmptyLines'));
      return;
    }
    if (!window.confirm(t('sap.form.confirmClose'))) return;
    setErrorMsg(null);
    try {
      const payload = buildPayload();
      await updateMut.mutateAsync(payload);
      await closeMut.mutateAsync();
      navigate('/documents/sap');
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  async function handleDelete() {
    if (!id) return;
    if (!window.confirm(t('sap.list.confirmDelete', { name: draftName || `SAP` }))) return;
    try {
      await deleteMut.mutateAsync(id);
      navigate('/documents/sap');
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  const isSubmitting = createMut.isPending || updateMut.isPending || closeMut.isPending;

  if (isEdit && sapQ.isLoading) {
    return <p className="text-sm text-slate-500">{t('common.loading')}</p>;
  }
  if (isEdit && sapQ.error) {
    return (
      <p className="text-sm text-red-600">
        {t('sap.form.errorLoad')}: {(sapQ.error as Error).message}
      </p>
    );
  }

  const titleStatus = isEdit
    ? sapQ.data?.code
      ? `SAP ${sapQ.data.code}`
      : (sapQ.data?.draftName ?? t('sap.form.titleEdit'))
    : t('sap.form.titleNew');

  return (
    <div className="space-y-4">
      <BackToStockMovementsLink />
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">{titleStatus}</h2>
        <div className="flex items-center gap-2">
          {isEdit && sapQ.data && (
            <span
              className={
                sapQ.data.status === 'DRAFT'
                  ? 'rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700'
                  : 'rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700'
              }
            >
              {sapQ.data.status === 'DRAFT' ? t('sap.list.statusDraft') : t('sap.list.statusClosed')}
            </span>
          )}
          <Button variant="secondary" onClick={() => navigate('/documents/sap')}>
            {t('common.back')}
          </Button>
        </div>
      </div>

      {errorMsg && (
        <Card>
          <CardContent>
            <p className="text-sm text-red-600">{errorMsg}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('sap.form.sectionHeader')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="draftName">{t('sap.fields.draftName')} *</Label>
            <Input
              id="draftName"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder={t('sap.form.draftNamePlaceholder')}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="responsable">{t('sap.fields.responsable')}</Label>
            <Input
              id="responsable"
              value={responsable}
              onChange={(e) => setResponsable(e.target.value)}
              placeholder={t('sap.form.responsablePlaceholder')}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="workDate">{t('sap.fields.workDate')} *</Label>
            <Input
              id="workDate"
              type="date"
              value={workDate}
              onChange={(e) => setWorkDate(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('sap.fields.totQty')}</Label>
            <p className="font-mono text-sm">{totQty.toFixed(2)}g</p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="notes">{t('sap.fields.notes')}</Label>
            <textarea
              id="notes"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={readOnly}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('sap.form.sectionLines')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">{t('sap.form.linesHint')}</p>
          {righe.length === 0 && (
            <p className="text-sm text-slate-500">{t('sap.form.emptyLines')}</p>
          )}
          {righe.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-2 py-2">{t('sap.fields.lotto')}</th>
                  <th className="px-2 py-2">{t('sap.fields.prodottoOverride')}</th>
                  <th className="px-2 py-2 text-right">{t('sap.fields.qty')} (g)</th>
                  <th className="px-2 py-2">{t('sap.fields.note')}</th>
                  <th className="px-2 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {righe.map((row) => (
                  <tr key={row.key} className="border-b border-slate-100">
                    <td className="px-2 py-1">
                      <LotPicker
                        value={row.lotId}
                        valueCode={row.lotCode}
                        onChange={(id2, code) =>
                          updateRiga(row.key, {
                            lotId: id2,
                            lotCode: code,
                            productId: null,
                            productCode: null,
                            productName: null,
                          })
                        }
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <ProductPicker
                        value={row.productId ?? ''}
                        valueCode={row.productCode}
                        valueName={row.productName}
                        onChange={(id2, code, name) =>
                          updateRiga(row.key, { productId: id2, productCode: code, productName: name })
                        }
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      <input
                        type="number"
                        step="0.0001"
                        className="w-24 rounded border border-slate-300 px-2 py-1 text-right text-xs"
                        value={row.qty}
                        onChange={(e) => updateRiga(row.key, { qty: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        placeholder={t('sap.form.notePlaceholder')}
                        className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                        value={row.note ?? ''}
                        onChange={(e) => updateRiga(row.key, { note: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => removeRiga(row.key)}
                          className="text-red-600 hover:underline text-xs"
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!readOnly && (
            <Button variant="secondary" size="sm" onClick={addRiga}>
              + {t('sap.form.addRiga')}
            </Button>
          )}
        </CardContent>
      </Card>

      {!readOnly && (
        <div className="flex justify-between gap-2">
          <div>
            {isEdit && (
              <Button
                variant="secondary"
                onClick={handleDelete}
                disabled={isSubmitting}
                className="!bg-red-50 !text-red-700 !border-red-200 hover:!bg-red-100"
              >
                {t('common.delete')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleSave} disabled={isSubmitting}>
              {t('sap.form.saveButton')}
            </Button>
            {isEdit && (
              <Button onClick={handleClose} disabled={isSubmitting}>
                {t('sap.form.closeButton')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
